import { MergeCases } from './action-types';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../core/api/api';
import * as commonEnums from '../enums/index';
import * as appServiceLines from '../core/service-lines';
import HttpMethods from '../enums/http-methods';
import { fetchActivity, getDocuments } from './code-bench.actions';
import { amplifyFeedback } from './ui.actions';
import { caseRunAvailable } from './launch-page.action';
import { navigationIntent } from './navigation-intent.action';

const REQUEST_INIT_OPTIONS: RequestInit = {
  method: 'GET',
  cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
  redirect: 'follow',
  referrer: 'no-referrer'
};

export const showMergeCasesModal = createAction<CMxCommonApp.MergeCaseModalProps>(
  MergeCases.SHOW_MERGE_CASES_MODAL
);

export const hideMergeCasesModal = createAction<void>(
  MergeCases.HIDE_MERGE_CASES_MODAL
);

export const setActiveCaseRun = createAction<string>(
  MergeCases.SET_ACTIVE_CASE_RUN
);

export const toggleMergeableCaseSelection = createAction<string>(
  MergeCases.TOGGLE_MERGEABLE_CASE_SELECTION
);

export const fetchMergeableCases = createAsyncThunk<
  CMxAPI.CaseRunV2[],
  CMxAPI.MergeableCasesParams,
  { rejectValue: CMxCommonApp.CMxError }
>(
  MergeCases.FETCH_MERGEABLE_CASES,
  async ({ encounterId, serviceLine }, _thunkAPI) => {
    try {
      const result = await api.requestJSON<CMxAPI.CaseRunV2[]>(
        `/coding-api/${serviceLine}/mergeablecases/v1?encounterId=${encounterId}`,
        {
          method: HttpMethods.GET
        }
      );

      return result;
    } catch (error) {
      console.error(error);
    }
  }
);

export const getDocumentsByCaseRun = createAsyncThunk<
  { htmlNotes: string[]; plainTextNotes: string[] },
  CMxAPI.CaseRun,
  { rejectValue: CMxCommonApp.SubmitError }
>(MergeCases.GET_DOCUMENTS_AS_HTML, async (caseRun, _thunkAPI) => {
  const tenantInfo = await api.requestJSON<CMxAPI.Organization>(
    `/organization/by-name/${caseRun.tenant_name}/v1`
  );

  const docs = await getDocuments(caseRun, tenantInfo);

  let htmlNotes: string[] = [];
  let plainTextNotes: string[] = [];

  for (const doc of docs) {
    const { htmlNotesResult, plainTextNotesResult } = await downloadDoc(doc);
    htmlNotes.push(htmlNotesResult);
    plainTextNotes.push(plainTextNotesResult);
    const { id, mimeType } = doc;

    if (mimeType === 'text/html' || mimeType === 'text/plain') {
      const notes = caseRun?.notes as CMxAPI.Note[];
      const caseRunNote = notes?.find(note => {
        return note.unique_document_number === doc.uniqueDocumentNumber;
      });

      const endpoint = `/documents/download/${id}/v1`;
      const content = await api.request(endpoint);
      const text = await content.text();

      if (mimeType === 'text/html') {
        // @ts-expect-error htmlNotes item shape extends string
        htmlNotes.push({
          description: caseRunNote?.description ?? doc.documentDescription,
          content: text,
          filename: caseRunNote?.note_specialty_designation ?? 'Document',
          type: doc.documentType,
          document_id: doc.documentId,
          id,
          filetype: mimeType,
          segments: [],
          spans: []
        });
      }

      if (mimeType === 'text/plain') {
        // @ts-expect-error plainTextNotes item shape extends string
        plainTextNotes.push({
          description: caseRunNote?.description ?? doc.documentDescription,
          content: text,
          filename: caseRunNote?.note_specialty_designation ?? 'Document',
          type: doc.documentType,
          document_id: doc.documentId,
          id,
          filetype: mimeType,
          segments: [],
          spans: []
        });
      }
    }
  }

  return { htmlNotes, plainTextNotes };
});

export const downloadDoc = async (doc: CMxAPI.DocumentMetadata) => {
  let htmlNotesResult: string = '';
  let plainTextNotesResult: string = '';

  const { id, mimeType } = doc;

  if (mimeType === 'text/html' || mimeType === 'text/plain') {
    const endpoint = `/documents/download/${id}/v1`;
    const content = await api.request(endpoint);
    const text = await content.text();

    if (mimeType === 'text/html') {
      htmlNotesResult = text;
    }

    if (mimeType === 'text/plain') {
      plainTextNotesResult = text;
    }
  }

  return {
    htmlNotesResult,
    plainTextNotesResult
  };
};

export const requestMergeCases = createAsyncThunk<
  CMxAPI.MergeCasesResult,
  CMxAPI.MergeCasesParams,
  { rejectValue: CMxCommonApp.SubmitError }
>(
  MergeCases.FETCH_MERGE_CASES,
  async (
    {
      currentCaseRun,
      casesToMerge,
      cursorAPIContext,
      hasAmplifyCaseRunV2Access
    },
    { dispatch }
  ) => {
    try {
      const mergeResult = await api.requestJSON<CMxAPI.MergeCasesResult>(
        `/coding-api/processinstance/${currentCaseRun.service_line}/${currentCaseRun.process_instance_guid}/merge/v1`,
        {
          ...REQUEST_INIT_OPTIONS,
          method: HttpMethods.POST,
          body: JSON.stringify({
            componentCases: casesToMerge.map(caseToMerge => {
              return {
                caseUid: caseToMerge.case_id,
                processInstanceGuid: caseToMerge.process_instance_guid,
                primary: caseToMerge.primary
              };
            })
          })
        }
      );

      const { serviceLine, caseUid, guid } = mergeResult;

      if (serviceLine && caseUid && guid) {
        setTimeout(() => {
          dispatch(
            launchInAmplify({
              isCodingQuality: false,
              serviceLine,
              caseId: caseUid,
              processId: guid.uuid,
              cursorAPIContext,
              hasAmplifyCaseRunV2Access
            }) as any
          );
        }, 5000);
      }

      return mergeResult;
    } catch (error) {
      throw error;
    }
  }
);

export const splitCase = createAsyncThunk<
  AmplifyAPI.ProcessInstance,
  AmplifyAPI.ProcessInstance,
  { rejectValue: CMxCommonApp.CMxError }
>(MergeCases.SPLIT_CASE, async (processInstance, { dispatch }) => {
  try {
    const updatedProcessInstance = await api.requestJSON<AmplifyAPI.ProcessInstance>(
      `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/split/v1`,
      {
        ...REQUEST_INIT_OPTIONS,
        method: HttpMethods.POST
      }
    );

    const serviceLineConfig = appServiceLines.determineServiceLineConfig({
      name: processInstance.serviceLine
    });
    const url = `${serviceLineConfig.path}/caseinstance/${updatedProcessInstance.caseUid}/details/${serviceLineConfig.caseDetailVersion}?evidence=true&processTypes=PREDICTION&processTypes=ADDENDUM`;

    const withDetails = await api.requestJSON<CMxAPI.Case>(url);
    let activeItem = withDetails.caseRuns.find(
      caseRun =>
        caseRun.process_instance_guid === updatedProcessInstance.guid.uuid
    );
    if (!!activeItem) {
      dispatch(caseRunAvailable(activeItem));
    }

    dispatch(fetchActivity(updatedProcessInstance) as any);
    return updatedProcessInstance;
  } catch (error) {
    dispatch(
      amplifyFeedback({
        message: 'There were issues while trying to split the case.',
        meta: commonEnums.FeedbackMeta.ERROR,
        dismissable: true
      })
    );
  }
  return processInstance;
});

export const launchInAmplify = createAsyncThunk<
  void,
  CMxCommonApp.LaunchWorkItemConfig,
  { rejectValue: CMxCommonApp.CMxError }
>(
  MergeCases.LAUNCH_IN_AMPLIFY,
  async (amplifyLaunchConfig: CMxCommonApp.LaunchWorkItemConfig, { dispatch }) => {
    dispatch(
      navigationIntent({
        intent: 'CASE_DETAIL',
        url: `/cmx/worklist/${amplifyLaunchConfig.serviceLine}/case/${
          amplifyLaunchConfig.caseId
        }/case-run/${amplifyLaunchConfig.processId}/${
          amplifyLaunchConfig.hasAmplifyCaseRunV2Access ? 'v2/' : ''
        }`,
        state: { ...amplifyLaunchConfig?.cursorAPIContext }
      })
    );
  }
);
